import React from "react";
import {
  Button,
  ButtonGroup,
  Icon,
  InputGroup,
  Popover,
  Position,
  Tag,
  Spinner,
} from "@blueprintjs/core";
import { observable, action, computed } from "mobx";
import { observer } from "mobx-react";
import PropTypes from "prop-types";
import { Api } from "services/Api";
import axios from "axios";

const selectAllId = "selectAll";

const initials = (name = "") =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join("");

@observer
class TechnicianPicker extends React.Component {
  @observable loading = true;
  @observable failed = false;
  @observable allOptions = [];
  @observable statusFilter = "all";
  @observable roleFilter = "all";
  @observable searchQuery = "";
  @observable selectedSkills = [];
  @observable skillsExpanded = true;

  // Normalize selectedOptions to an array of IDs/values for comparison
  normalizeToValueArray(selectedOptions) {
    if (!selectedOptions) return [];

    if (Array.isArray(selectedOptions)) {
      return selectedOptions.map((option) => {
        if (
          typeof option === "object" &&
          option !== null &&
          option.value !== undefined
        ) {
          return option.value;
        }
        return option;
      });
    }

    if (typeof selectedOptions === "object" && selectedOptions !== null) {
      return Object.values(selectedOptions).map((val) => {
        if (
          typeof val === "object" &&
          val !== null &&
          val.value !== undefined
        ) {
          return val.value;
        }
        return val;
      });
    }

    return selectedOptions ? [selectedOptions] : [];
  }

  // Normalize selectedOptions to an array of option objects for display/processing
  normalizeToOptionArray(selectedOptions) {
    if (!selectedOptions) return [];

    if (Array.isArray(selectedOptions)) {
      return selectedOptions.map((option) => {
        if (
          typeof option === "object" &&
          option !== null &&
          option.value !== undefined
        ) {
          return option;
        }
        const foundOption = this.allOptions.find((opt) => opt.value === option);
        return foundOption || { value: option, label: String(option) };
      });
    }

    if (typeof selectedOptions === "object" && selectedOptions !== null) {
      return Object.values(selectedOptions).map((val) => {
        if (
          typeof val === "object" &&
          val !== null &&
          val.value !== undefined
        ) {
          return val;
        }
        const foundOption = this.allOptions.find((opt) => opt.value === val);
        return foundOption || { value: val, label: String(val) };
      });
    }

    // A single id (e.g. from a link's ?where[created_by_id]=…) still counts
    // before the people list loads, like the array case above.
    const foundOption = this.allOptions.find(
      (opt) => opt.value === selectedOptions
    );
    return [foundOption || { value: selectedOptions, label: String(selectedOptions) }];
  }

  // Every technician option, without the Select All pseudo option.
  @computed
  get technicianOptions() {
    return this.allOptions.filter((option) => option.value !== selectAllId);
  }

  // Skills are free-form per technician, so the filter's chips are whatever
  // the loaded technicians actually carry. No skills anywhere -> no filter.
  @computed
  get availableSkills() {
    const skills = new Set();
    this.technicianOptions.forEach((option) => {
      (option.skills || []).forEach((skill) => skills.add(skill));
    });
    return Array.from(skills).sort((a, b) =>
      a.toLowerCase().localeCompare(b.toLowerCase())
    );
  }

  // Technician options matching the current search / role / status filters.
  @computed
  get filteredOptions() {
    const query = this.searchQuery.trim().toLowerCase();
    return this.technicianOptions.filter((option) => {
      if (this.statusFilter === "active" && !option.is_active) {
        return false;
      }
      if (this.statusFilter === "inactive" && option.is_active) {
        return false;
      }

      if (this.roleFilter !== "all") {
        const role = (option.role || "").toLowerCase();
        if (role !== this.roleFilter) {
          return false;
        }
      }

      if (this.selectedSkills.length > 0) {
        const skills = option.skills || [];
        const matchesSkill = this.selectedSkills.some((skill) =>
          skills.includes(skill)
        );
        if (!matchesSkill) {
          return false;
        }
      }

      if (!query) {
        return true;
      }

      return (
        (option.label && option.label.toLowerCase().includes(query)) ||
        (option.searchText && option.searchText.includes(query))
      );
    });
  }

  // Plain getters (not @computed): these read props, which mobx does not
  // reliably track, so caching them could go stale.
  get selectedValueSet() {
    return new Set(
      this.normalizeToValueArray(this.props.selectedOptions).filter(
        (value) => value !== selectAllId
      )
    );
  }

  get allVisibleSelected() {
    const visible = this.filteredOptions;
    return (
      visible.length > 0 &&
      visible.every((option) => this.selectedValueSet.has(option.value))
    );
  }

  get isMultiSelect() {
    return !this.props.maximumSelections || this.props.maximumSelections > 1;
  }

  @action
  onTechnicianLoad(response) {
    let {
      props: { selectedOptions, includeSelectAll },
    } = this;

    const normalizedSelectedOptions =
      this.normalizeToValueArray(selectedOptions);

    this.allOptions = [];
    if (includeSelectAll) {
      this.allOptions = [
        {
          label: "Select All",
          value: selectAllId,
          selected: normalizedSelectedOptions.includes(selectAllId),
          is_active: true,
          searchText: "",
        },
      ];
    }
    const options = response.data.data.map((technician) => {
      const fullName = technician.attributes.full_name || "";
      const role = technician.attributes.role;
      const email = technician.attributes.email || "";
      let label = `${fullName}`;
      if (role && role !== null) {
        label = `${fullName} (${role})`;
      }
      const isActive =
        technician.attributes.is_active == null
          ? true
          : !!technician.attributes.is_active;
      const title = technician.attributes.title || "";
      const skills = (technician.attributes.skills || []).filter(Boolean);
      return {
        label: label,
        value: technician.id || "0",
        selected: normalizedSelectedOptions.includes(technician.id),
        is_active: isActive,
        fullName: fullName,
        role: role || "",
        email: email,
        title: title,
        skills: skills,
        searchText: `${fullName} ${role || ""} ${email} ${title} ${skills.join(
          " "
        )}`.toLowerCase(),
      };
    });
    this.allOptions = [...this.allOptions, ...options];
    this.failed = false;
    this.loading = false;
  }

  @action
  onTechnicianLoadFail(error) {
    console.log("error: ", error);
    if (!axios.isCancel(error)) {
      this.loading = false;
      this.failed = true;
    }
  }

  @action
  setStatusFilter(statusFilter) {
    this.statusFilter = statusFilter;
  }

  @action
  setRoleFilter(roleFilter) {
    this.roleFilter = roleFilter;
  }

  @action
  setSearchQuery(searchQuery) {
    this.searchQuery = searchQuery;
  }

  @action
  toggleSkill(skill) {
    if (this.selectedSkills.includes(skill)) {
      this.selectedSkills = this.selectedSkills.filter(
        (selected) => selected !== skill
      );
    } else {
      this.selectedSkills = [...this.selectedSkills, skill];
    }
  }

  @action
  toggleSkillsExpanded() {
    this.skillsExpanded = !this.skillsExpanded;
  }

  // With showSelected the chosen technicians are listed under the picker, so
  // their names, titles and skills are needed before the picker is opened.
  componentDidMount() {
    if (this.props.showSelected || this.props.showName) {
      this.loadTechnicians();
    }
  }

  onRemoveOption(value) {
    this.emitChange(
      this.normalizeToOptionArray(this.props.selectedOptions).filter(
        (option) => option.value !== selectAllId && option.value !== value
      )
    );
  }

  // The chosen technicians: name, title and skills, each with a remove button.
  renderSelectedList(selectedOptions) {
    if (selectedOptions.length === 0) {
      return null;
    }
    return (
      <ul className="tech-picked">
        {selectedOptions.map((selected) => {
          const option =
            this.technicianOptions.find((opt) => opt.value === selected.value) ||
            selected;
          const known = Boolean(option.fullName);
          const name = known ? option.fullName : this.loading ? "Loading…" : "Unknown technician";
          return (
            <li key={option.value} className="tech-picked__item">
              <span className="tech-picked__avatar" aria-hidden="true">
                {known ? initials(name) : ""}
              </span>
              <span className="tech-picked__text">
                <span className="tech-picked__name">
                  {name}
                  {known && !option.is_active && (
                    <span className="tech-picked__badge">Inactive</span>
                  )}
                </span>
                {(option.title || option.role) && (
                  <span className="tech-picked__title">
                    {option.title ||
                      option.role.charAt(0).toUpperCase() + option.role.slice(1)}
                  </span>
                )}
                {option.skills && option.skills.length > 0 && (
                  <span className="tech-picked__skills">
                    {option.skills.map((skill) => (
                      <span key={skill} className="tech-picked__skill">
                        {skill}
                      </span>
                    ))}
                  </span>
                )}
              </span>
              <button
                type="button"
                className="tech-picked__remove"
                onClick={() => this.onRemoveOption(option.value)}
                title={`Remove ${known ? name : "this technician"}`}
                aria-label={`Remove ${known ? name : "this technician"}`}
                disabled={this.props.disabled}
              >
                <Icon icon="cross" iconSize={14} />
              </button>
            </li>
          );
        })}
      </ul>
    );
  }

  loadTechnicians() {
    this.loading = true;
    this.failed = false;
    const roles = this.props.includeAdmins
      ? ["admin", "technician"]
      : ["technician"];
    let promise = Api.post("/users/search", {
      role: roles,
      include_unassigned: this.props.includeUnassigned,
    });
    promise
      .then(this.onTechnicianLoad.bind(this))
      .catch(this.onTechnicianLoadFail.bind(this));
    return promise;
  }

  // Selections that the active filters are currently hiding must survive any
  // change made from the visible list.
  hiddenSelectedOptions() {
    const visibleValues = new Set(
      this.filteredOptions.map((option) => option.value)
    );
    return this.normalizeToOptionArray(this.props.selectedOptions).filter(
      (option) =>
        option.value !== selectAllId && !visibleValues.has(option.value)
    );
  }

  emitChange(selectedOptions) {
    if (this.props.onChange) {
      this.props.onChange(
        selectedOptions.map((option) => ({ ...option, selected: true })),
        this.allOptions
      );
    }
  }

  onToggleOption = (option) => {
    const { maximumSelections } = this.props;
    const currentSelected = this.normalizeToOptionArray(
      this.props.selectedOptions
    ).filter((selected) => selected.value !== selectAllId);

    if (this.selectedValueSet.has(option.value)) {
      this.emitChange(
        currentSelected.filter((selected) => selected.value !== option.value)
      );
      return;
    }

    const nextSelected = [...currentSelected, option];
    if (maximumSelections && nextSelected.length > maximumSelections) {
      // Matches the previous behaviour: exceeding the cap replaces the
      // selection with the option that was just clicked.
      this.emitChange([option]);
      return;
    }
    this.emitChange(nextSelected);
  };

  onToggleSelectAll = () => {
    if (!this.isMultiSelect) return;
    if (this.allVisibleSelected) {
      // Unticking Select All means "none selected", including anyone the
      // active filters are hiding — otherwise the box reads unchecked while
      // the footer still counts hidden selections, and re-ticking it would
      // add the visible rows on top of them.
      this.emitChange([]);
    } else {
      this.emitChange([...this.hiddenSelectedOptions(), ...this.filteredOptions]);
    }
  };

  onReset = () => {
    this.setSearchQuery("");
    this.setRoleFilter("all");
    this.setStatusFilter("all");
    this.clearSkills();
    this.emitChange([]);
  };

  @action
  clearSkills() {
    this.selectedSkills = [];
  }

  renderSegmentedFilter(label, value, onSelect, segments) {
    return (
      <div className="technician-picker-segment-row">
        <span className="technician-picker-segment-label">{label}</span>
        <ButtonGroup fill={true} className="technician-picker-segments">
          {segments.map((segment) => (
            <Button
              key={segment.value}
              small={true}
              active={value === segment.value}
              onClick={() => onSelect(segment.value)}
            >
              {segment.label}
            </Button>
          ))}
        </ButtonGroup>
      </div>
    );
  }

  renderFilters() {
    return (
      <div className="technician-picker-filters">
        <InputGroup
          className="technician-picker-search"
          leftIcon="search"
          placeholder="Search name, title, or skill..."
          value={this.searchQuery}
          onChange={(event) => this.setSearchQuery(event.target.value)}
        />
        {this.props.includeAdmins &&
          this.renderSegmentedFilter(
            "Role",
            this.roleFilter,
            this.setRoleFilter.bind(this),
            [
              { label: "All", value: "all" },
              { label: "Technician", value: "technician" },
              { label: "Admin", value: "admin" },
            ]
          )}
        {this.renderSegmentedFilter(
          "Status",
          this.statusFilter,
          this.setStatusFilter.bind(this),
          [
            { label: "All", value: "all" },
            { label: "Active", value: "active" },
            { label: "Inactive", value: "inactive" },
          ]
        )}
        {this.renderSkillsFilter()}
      </div>
    );
  }

  renderSkillsFilter() {
    const availableSkills = this.availableSkills;
    if (availableSkills.length === 0) {
      return null;
    }
    return (
      <div className="technician-picker-skills">
        <div
          className="technician-picker-skills-header"
          onClick={() => this.toggleSkillsExpanded()}
        >
          <span className="technician-picker-skills-title">
            <Icon icon="wrench" iconSize={13} />
            <span>Skills</span>
          </span>
          <Icon icon={this.skillsExpanded ? "chevron-up" : "chevron-down"} />
        </div>
        {this.skillsExpanded && (
          <div className="technician-picker-skill-chips">
            {availableSkills.map((skill) => (
              <span
                key={skill}
                className={`technician-picker-skill-chip${
                  this.selectedSkills.includes(skill) ? " is-selected" : ""
                }`}
                onClick={() => this.toggleSkill(skill)}
              >
                {skill}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }

  // A plain element rather than Blueprint's Checkbox: the app's global styles
  // render bp3 checkboxes as circles, and Blueprint spreads click handlers onto
  // the hidden input, which double-fired against the row handler.
  renderCheck(checked) {
    return (
      <span
        className={`technician-picker-check${checked ? " is-checked" : ""}`}
      >
        {checked && <Icon icon="tick" iconSize={10} />}
      </span>
    );
  }

  renderSelectAll() {
    return (
      <div
        className="technician-picker-select-all"
        onClick={this.onToggleSelectAll}
      >
        <div className="technician-picker-select-all-label">
          {this.renderCheck(this.allVisibleSelected)}
          <span>Select all</span>
        </div>
        <span className="technician-picker-shown">
          {this.filteredOptions.length} shown
        </span>
      </div>
    );
  }

  renderOption(option, selectedValues) {
    // Single-select pickers close on pick (as before). Multi-select panels stay
    // open so several technicians can be ticked in one go.
    const dismissClass =
      this.props.shouldDismissPopover === false || this.isMultiSelect
        ? ""
        : " bp3-popover-dismiss";
    const isSelected = selectedValues.has(option.value);
    return (
      <div
        key={option.value}
        className={`technician-picker-option${
          isSelected ? " is-selected" : ""
        }${dismissClass}`}
        onClick={() => this.onToggleOption(option)}
      >
        {this.renderCheck(isSelected)}
        <div className="technician-picker-option-body">
          <div className="technician-picker-option-name">
            <span>{option.fullName || option.label}</span>
            {option.role && (
              <span className="technician-picker-role-badge">
                {option.role}
              </span>
            )}
            {!option.is_active && (
              <span className="technician-picker-role-badge is-inactive">
                inactive
              </span>
            )}
          </div>
          {(option.title || option.email) && (
            <div className="technician-picker-option-meta">
              {option.title || option.email}
            </div>
          )}
          {option.skills && option.skills.length > 0 && (
            <div className="technician-picker-option-skills">
              {option.skills.map((skill) => (
                <span key={skill} className="technician-picker-option-skill">
                  {skill}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  get canReset() {
    return (
      this.selectedValueSet.size > 0 ||
      this.searchQuery.trim().length > 0 ||
      this.roleFilter !== "all" ||
      this.statusFilter !== "all" ||
      this.selectedSkills.length > 0
    );
  }

  renderFooter(selectedCount) {
    return (
      <div className="technician-picker-footer">
        <span>{selectedCount} selected</span>
        <Button
          minimal={true}
          small={true}
          disabled={!this.canReset}
          onClick={this.onReset}
        >
          Reset
        </Button>
      </div>
    );
  }

  renderContent() {
    if (this.failed) {
      return (
        <div className="technician-picker">
          {this.renderFilters()}
          <div className="bp3-callout bp3-intent-danger bp3-icon-info-sign">
            Had a problem loading technicians. Please close and try again.
          </div>
        </div>
      );
    }

    if (this.loading) {
      return (
        <div className="technician-picker">
          {this.renderFilters()}
          <div className="technician-picker-loading">
            <Spinner />
          </div>
        </div>
      );
    }

    const listOptions = this.filteredOptions;
    const selectedValues = this.selectedValueSet;
    const selectedCount = selectedValues.size;
    return (
      <div className="technician-picker">
        {this.renderFilters()}
        {this.props.includeSelectAll &&
          listOptions.length > 0 &&
          this.renderSelectAll()}
        {listOptions.length > 0 ? (
          <div className="technician-picker-list">
            {listOptions.map((option) =>
              this.renderOption(option, selectedValues)
            )}
          </div>
        ) : (
          <div className="technician-picker-empty">No technicians found</div>
        )}
        {this.isMultiSelect && this.renderFooter(selectedCount)}
      </div>
    );
  }

  render() {
    let {
      props: { selectedOptions: propsSelectedOptions, disabled },
    } = this;

    const selectedOptions = this.normalizeToOptionArray(
      propsSelectedOptions
    ).filter((option) => option.value !== selectAllId);
    const technicianOptions = this.technicianOptions;

    let tagText = "0";
    if (selectedOptions && selectedOptions.length > 0) {
      if (
        technicianOptions.length > 0 &&
        selectedOptions.length === technicianOptions.length
      ) {
        tagText = "All";
      } else if (this.props.hideHeading && selectedOptions.length === 1) {
        tagText = selectedOptions[0].label;
      } else {
        tagText = selectedOptions.length;
      }
    }
    let heading = "Technicians";
    if (this.props.showCreatedBy) {
      heading = "Created By";
    } else if (this.props.showUser) {
      heading = "User";
    }
    // showName: one person picked -> their name on the button, not a count.
    const namedOption =
      this.props.showName && selectedOptions.length === 1
        ? technicianOptions.find((option) => option.value === selectedOptions[0].value)
        : null;
    const named = namedOption && namedOption.fullName ? namedOption : null;
    const picker = (
      <Popover
        minimal={true}
        onOpening={this.loadTechnicians.bind(this)}
        content={this.renderContent()}
        position={Position.BOTTOM_LEFT}
        usePortal={true}
        className="actionItem"
        popoverClassName="technician-picker-popover"
        disabled={disabled}
      >
        {named ? (
          <Button rightIcon="caret-down" className="tech-picker-named">
            <span className="tech-picker-named__avatar" aria-hidden="true">{initials(named.fullName)}</span>
            <span className="tech-picker-named__name">{named.fullName}</span>
          </Button>
        ) : (
          <Button rightIcon="caret-down">
            {!this.props.hideHeading && heading}
            <span>&nbsp;</span>
            <Tag minimal={true} round={true}>
              {tagText}
            </Tag>
          </Button>
        )}
      </Popover>
    );
    if (!this.props.showSelected) {
      return picker;
    }
    return (
      <div className="tech-picker-with-list">
        {picker}
        {this.renderSelectedList(selectedOptions)}
      </div>
    );
  }
}

TechnicianPicker.propTypes = {
  selectedOptions: PropTypes.oneOfType([PropTypes.array, PropTypes.string])
    .isRequired,
  onChange: PropTypes.func,
  includeUnassigned: PropTypes.bool,
  includeSelectAll: PropTypes.bool,
  includeAdmins: PropTypes.bool,
  maximumSelections: PropTypes.number,
  shouldDismissPopover: PropTypes.bool,
  // List the chosen technicians (name, title, skills) under the picker.
  showSelected: PropTypes.bool,
  // With one person picked, show their name on the button instead of a count.
  showName: PropTypes.bool,
};

export default TechnicianPicker;
